import React, { Component } from 'react';
import { getPaymentDetailsFieldsOrder } from '@wix/cashier-common/dist/src/utils/getPaymentDetailsFieldsOrder';
import { type CountryAlpha3 } from '@wix/cashier-common/dist/src/utils/countryCodes';
import { type ICountryEntry } from '../../types/CountryEntry';
import { DeviceTypeEnum } from '../../types/DeviceType';
import { type UserInfo } from '../../types/UserInfo';
import { validators } from '../../utils/validations';
import { t } from '../../utils/i18n';
import { DH } from '../../utils/DataHook';
import { CountryContainer, Label, Line } from './Elements';
import { Autocomplete } from './Elements/Autocomplete';
import { TextField } from './TextField';
import { type UserInfoFieldType } from './types';

export interface UserInfoFormViewProps {
  appearance?: DeviceTypeEnum;
  countries: ICountryEntry[];
  initialUserInfo?: UserInfo;
  resolvedCountry?: string;
  onChange?(isValid: boolean, formData: any, isChangedProgrammatically?: boolean): void;
  disabled: boolean;
}

interface IFieldEntry {
  value: string;
  isValid: boolean;
}

type UserInfoFormViewState = Record<UserInfoFieldType, IFieldEntry>;

const fieldsSchema = {
  firstName: {
    name: 'firstName',
    type: 'text',
    validator: validators.default,
  },
  lastName: {
    name: 'lastName',
    type: 'text',
    validator: validators.default,
  },
  email: {
    name: 'email',
    type: 'email',
    validator: validators.email,
  },
  country: {
    name: 'country',
    type: 'text',
    validator: validators.allowedList.bind(null, []),
  },
  phone: {
    name: 'phone',
    type: 'tel',
    validator: validators.alwaysValid,
  },
};

export class UserInfoFormView extends Component<UserInfoFormViewProps, UserInfoFormViewState> {
  static defaultProps: Partial<UserInfoFormViewProps> = {
    appearance: DeviceTypeEnum.desktop,
    initialUserInfo: {
      firstName: '',
      lastName: '',
      country: '',
      email: '',
      phone: '',
    },
    onChange: () => void 0,
  };

  public componentAPI = {
    getFormData: (): UserInfo => ({
      firstName: this.state.firstName.value,
      lastName: this.state.lastName.value,
      email: this.state.email.value,
      country: this.state.country.value as CountryAlpha3,
      phone: this.state.phone.value,
    }),
    validate: () => {
      const invalidFields = Object.entries(this.componentAPI.getFormData())
        .filter(([key, value]) => !fieldsSchema[key].validator(value))
        .map(([key]) => key);

      return {
        isValid: invalidFields.length === 0,
        invalidFields,
      };
    },
    markInvalidFields: (): void => {
      const state = {};
      Object.entries(this.componentAPI.getFormData()).forEach(([key, value]) => {
        state[key] = {
          ...this.state[key],
          isValid: fieldsSchema[key].validator(value),
        };
      });

      this.setState(state);
    },
  };

  constructor(props: UserInfoFormViewProps) {
    super(props);

    this.initCountriesValidator(props.countries);

    const countryValue = props.initialUserInfo?.country || props.resolvedCountry || '';

    this.state = {
      firstName: {
        value: props.initialUserInfo.firstName,
        isValid: undefined,
      },
      lastName: {
        value: props.initialUserInfo.lastName,
        isValid: undefined,
      },
      email: {
        value: props.initialUserInfo.email,
        isValid: undefined,
      },
      country: {
        value: countryValue,
        isValid: countryValue ? fieldsSchema.country.validator(countryValue) : undefined,
      },
      phone: {
        value: props.initialUserInfo.phone,
        isValid: true,
      },
    };
  }

  componentDidMount() {
    const isChangedProgrammatically = true;
    this.props.onChange(
      this.componentAPI.validate().isValid,
      this.componentAPI.getFormData(),
      isChangedProgrammatically,
    );
  }

  private initCountriesValidator(countries: ICountryEntry[]) {
    const allowedList = Object.values(countries).map(({ key }) => key);
    fieldsSchema.country.validator = validators.allowedList.bind(null, allowedList);
  }

  changeCountry = (value: string) => {
    const foundValue = this.props.countries.find((entry) => entry.key === value || entry.displayName === value);

    value = (foundValue && foundValue.key) || value;
    this.onChange({
      name: 'country',
      value,
      isValid: fieldsSchema.country.validator(value),
    });
  };

  onCountryBlur = (e) => {
    this.changeCountry(e.target.value);
  };

  onCountryChange = (e) => {
    this.changeCountry(e.target.value);
  };

  onChange = (
    { name, value, isValid }: { name: UserInfoFieldType; value: string; isValid?: boolean },
    isChangedProgrammatically?: boolean,
  ): void => {
    this.setState(
      (prevState) => {
        return {
          ...prevState,
          [name]: {
            value,
            isValid,
          },
        };
      },
      () => {
        this.props.onChange(
          this.componentAPI.validate().isValid,
          this.componentAPI.getFormData(),
          isChangedProgrammatically,
        );
      },
    );
  };

  renderCountryAutocomplete() {
    const { appearance, countries } = this.props;

    const options = countries
      .slice()
      .sort((entry1, entry2) => entry1.displayName.localeCompare(entry2.displayName))
      .map((entry) => ({
        key: entry.key,
        id: entry.key,
        isDisabled: false,
        isSelectable: true,
        value: entry.displayName || entry.key,
        render: (value: string) => value,
      }));

    const countryValue = this.state.country.value;

    const initialSelectedId = countryValue && options.some((opt) => opt.id === countryValue) ? countryValue : null;

    return (
      <Autocomplete
        appearance={appearance}
        disabled={this.props.disabled}
        data-hook={DH.UserInfoForm.CountryDropdown}
        placeholder={
          appearance === DeviceTypeEnum.desktop
            ? t('payments.app.userInfo.country.placeholder')
            : t('payments.app.userInfo.country')
        }
        options={options}
        aria-label={'combobox'}
        initialSelectedId={initialSelectedId}
        onSelect={(option) => this.changeCountry(option.id as string)}
        error={this.state.country.isValid === false}
        errorMessage={t('payments.app.userInfo.country.error')}
        onChange={this.onCountryChange}
        onBlur={this.onCountryBlur}
        inputProps={{
          id: 'country',
          required: true,
          'aria-required': true,
          'aria-invalid': this.state.country.isValid === false,
        }}
      />
    );
  }

  renderTextField(schema) {
    return (
      <TextField
        dataHook={DH.UserInfoForm.Field(schema.name)}
        key={schema.name}
        appearance={this.props.appearance}
        placeholder={t(`payments.app.userInfo.${schema.name}.placeholder`)}
        name={schema.name}
        type={schema.type}
        label={t(`payments.app.userInfo.${schema.name}`)}
        onChange={this.onChange}
        value={this.state[schema.name].value}
        isValid={this.state[schema.name].isValid}
        validate={schema.validator}
        disabled={this.props.disabled}
        errorMessage={t(`payments.app.userInfo.${schema.name}.error`)}
        required={schema.name !== 'phone'}
      />
    );
  }

  renderCountry() {
    const { appearance } = this.props;

    return (
      <CountryContainer appearance={appearance}>
        <Label htmlFor={'country'} appearance={appearance} title={t('payments.app.userInfo.country')} required />
        {this.renderCountryAutocomplete()}
      </CountryContainer>
    );
  }

  render() {
    const singleFieldInLine = this.props.appearance === 'mobile';
    const fieldsOrder = getPaymentDetailsFieldsOrder(this.state.country.value?.toLowerCase());
    const isLastNameFieldFirst = fieldsOrder.indexOf('lastName') < fieldsOrder.indexOf('firstName');
    return (
      <section data-hook={DH.UserInfoForm.Container}>
        <Line>{this.renderTextField(fieldsSchema.email)}</Line>

        {!singleFieldInLine && [
          <Line key="first">
            {isLastNameFieldFirst
              ? [this.renderTextField(fieldsSchema.lastName), this.renderTextField(fieldsSchema.firstName)]
              : [this.renderTextField(fieldsSchema.firstName), this.renderTextField(fieldsSchema.lastName)]}
          </Line>,
          <Line key="second">
            {this.renderCountry()}
            {this.renderTextField(fieldsSchema.phone)}
          </Line>,
        ]}

        {singleFieldInLine && [
          isLastNameFieldFirst
            ? [
                <Line key="second">{this.renderTextField(fieldsSchema.lastName)}</Line>,
                <Line key="first">{this.renderTextField(fieldsSchema.firstName)}</Line>,
              ]
            : [
                <Line key="first">{this.renderTextField(fieldsSchema.firstName)}</Line>,
                <Line key="second">{this.renderTextField(fieldsSchema.lastName)}</Line>,
              ],
          <Line key="third">{this.renderCountry()}</Line>,
          <Line key="fourth">{this.renderTextField(fieldsSchema.phone)}</Line>,
        ]}
      </section>
    );
  }
}
